/* eslint-disable consistent-return */
/* eslint-disable no-param-reassign */
import * as tus from 'tus-js-client';
import { v4 as uuidv4 } from 'uuid';
import DriveService from '@/modules/drive/services/DriveService';
import { useMainStore } from '@/store/mainStore';
import {
  ACCOUNT_STATUS, MEDIA_TYPES, MIXED_DATA_TYPE, UPLOAD_PLACES, UPLOAD_SOURCE,
} from '@/types/enums';
import {
  ICropCoordinates,
  IData,
  IFileUpload, IImageAddLabels, ILabelInput, IMedia, IMixedData, IResponse, ISearchLabelInput, ITagInput,
} from '@/types/interfaces';
import InputLabelsService from './InputLabelsService';
import LocalStorageService from './LocalStorageService';
import { IOnUploadUpdate } from '@/composables/UseUpload';
import ApiService from './ApiService';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import NotificationService from './NotificationService';
import SkeletonService from './SkeletonService';

const PARALEL_FILES_UPLOAD_SIZE = 3;

export default class UploadService {
  static isAbortUpload = false;

  private static listenUploadAborting(onUploadUpdate: () => IOnUploadUpdate, reject: (reason?: any) => void) {
    const interval = setInterval(() => {
      if (this.isAbortUpload) {
        if (onUploadUpdate().isUploadFinished()) {
          clearInterval(interval);
          reject();
        }
      }
    }, 500);

    return interval;
  }

  private static checkLabels(id: string, labelsId: number[]) {
    const driveStore = useDriveStore();
    return new Promise((resolve, reject) => {
      if (!labelsId.length) {
        return resolve(id);
      }

      DriveService.addImageLabels({
        id, description: '', labelIds: labelsId, photoCrop: null,
      } as IImageAddLabels)
        .then((response) => {
          driveStore.photos.data.find((item) => item.id === id)!.labels = response.results.data.labels;
          resolve(id);
        })
        .catch((e) => {
          console.error(`Labels are not added to file: ${id}`);
          reject(e);
        });
    });
  }

  private static async checkCrop(id: string, coodrinates?: ICropCoordinates) {
    if (coodrinates) {
      await this.cropPhoto(id, coodrinates);
    }
  }

  private static toFile(item: IFileUpload): File {
    return item.file;
  }

  public static toFileUploads(fileList: FileList | File[]): IFileUpload[] {
    const fileUploads: IFileUpload[] = [];

    for (let i = 0; i < fileList.length; i += 1) {
      fileUploads.push({ file: fileList[i], labels: [] });
    }

    return fileUploads;
  }

  private static async getItemLabels(item: IFileUpload, labels = [] as ISearchLabelInput[]): Promise<number[]> {
    if (!item.labels?.length) {
      return [];
    }

    const labelsHasId: ISearchLabelInput[] = labels.filter((el) => item.labels?.some((x) => x.text === el.name));
    const labelsNoId: ITagInput[] = item.labels?.filter((el) => !labelsHasId.some((hid) => hid.name === el.text)) || [];

    if (labelsNoId.length) {
      return Promise.all(labelsNoId.map(this.createLabel)).then((responseAll) => {
        const newIds: ILabelInput[] = responseAll.map((el) => el.results.data);
        labels.push(...newIds);
        return [...newIds.map((el) => el.id), ...labelsHasId.map((el) => el.id as number)];
      });
    }
    return [...labelsHasId.map((el) => el.id as number)];
  }

  private static createLabel(item: ITagInput): Promise<IResponse<IData<ILabelInput>>> {
    return InputLabelsService.createInputLabel(item.text);
  }

  private static tusFileUpload(file: File, onUploadUpdate: () => IOnUploadUpdate, folderId = '', isSingle: boolean, source: UPLOAD_SOURCE, isAvatar: boolean, place: string) {
    const mainStore = useMainStore();

    return new Promise<string>((resolve, reject) => {
      const dotIndex = file.name.lastIndexOf('.');
      const extension = dotIndex > -1 ? file.name.substring(dotIndex + 1) : '';
      const name = file.name.split('.').shift() as string;

      let isAborting = false;

      const uuid = uuidv4() as string;

      let prevUploaded = 0;

      // Create a new tus upload
      const upload = new tus.Upload(file, {
        endpoint: process.env.VUE_APP_UPLOAD_URL,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        chunkSize: 1048576 * 1, // 1024 * 1024 * 1 = 1 MB
        metadata: {
          authToken: LocalStorageService.getAuthToken(),
          extension,
          name,
          env: process.env.VUE_APP_TUS_ENV as string,
          language: 'en',
          accountId: mainStore.user.accountId,
          folderId,
          saveMode: 'RENAME',
          type: file.type,
          id: uuid,
          uploadType: isSingle ? 'SINGLE' : 'MULTIPLE',
          source,
          isAvatar: `${isAvatar}`,
          source_location: `${place}`,
        },
        onError(error) {
          console.error(`Failed because: ${error}`);
          return reject();
        },
        onProgress(bytesUploaded, bytesTotal) {
          const buferprevUploaded = prevUploaded;
          prevUploaded = bytesTotal - (bytesTotal - bytesUploaded);

          onUploadUpdate().getUploadInfo().totalBytesUploaded += (prevUploaded - buferprevUploaded);

          if (!isAborting && UploadService.isAbortUpload) {
            upload.abort(true);
            isAborting = true;
            reject();
          }
          // const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
        },
        onSuccess() {
          return resolve(uuid);
        },

      });

      // Check if there are any previous uploads to continue.
      upload.findPreviousUploads().then((previousUploads) => {
      // Found previous uploads so we select the first one.
        if (previousUploads.length) {
          upload.resumeFromPreviousUpload(previousUploads[0]);
        }

        // Start the upload
        upload.start();
      });
    });
  }

  private static toParalelUpload(filesToUpload: IFileUpload[]): IFileUpload[][] {
    const newArr: IFileUpload[][] = [];

    for (let i = 0; i < filesToUpload.length; i += 1) {
      if (i % PARALEL_FILES_UPLOAD_SIZE === 0) {
        newArr[Math.floor(i / PARALEL_FILES_UPLOAD_SIZE)] = [filesToUpload[i]];
      } else {
        newArr[Math.floor(i / PARALEL_FILES_UPLOAD_SIZE)][i % PARALEL_FILES_UPLOAD_SIZE] = filesToUpload[i];
      }
    }

    return newArr;
  }

  private static removeSkeletons(place: {location: UPLOAD_PLACES, id?: string}, amount = 1) {
    switch (place.location) {
      case UPLOAD_PLACES.CASE:
        SkeletonService.removeCaseSkeletons(place.id!, amount, MEDIA_TYPES.FILES);
        break;
      case UPLOAD_PLACES.CASE_PHOTO:
        SkeletonService.removeCaseSkeletons(place.id!, amount, MEDIA_TYPES.PHOTO);
        break;
      case UPLOAD_PLACES.DIALOG_PHOTO:
        SkeletonService.removeDialogSkeletons(place.id!, amount, MEDIA_TYPES.PHOTO);
        break;
      case UPLOAD_PLACES.FOLDER:
        SkeletonService.removeFolderSkeletons(place.id!, amount, MEDIA_TYPES.FILES);
        break;
      case UPLOAD_PLACES.FOLDER_PHOTO:
        SkeletonService.removeFolderSkeletons(place.id!, amount, MEDIA_TYPES.PHOTO);
        break;
      case UPLOAD_PLACES.MIXED:
        SkeletonService.removeMixedSkeletons(amount);
        break;
      case UPLOAD_PLACES.MIXED_PHOTO:
        SkeletonService.removeMixedSkeletons(amount);
        SkeletonService.removePhotoSkeletons(amount);
        break;
      case UPLOAD_PLACES.PHOTO:
        SkeletonService.removePhotoSkeletons(amount);
        break;
      default:
        break;
    }
  }

  private static setSkeletons(place: {location: UPLOAD_PLACES, id?: string}, amount: number) {
    switch (place.location) {
      case UPLOAD_PLACES.CASE:
        SkeletonService.setCaseSkeletons(place.id!, amount, MEDIA_TYPES.FILES);
        break;
      case UPLOAD_PLACES.CASE_PHOTO:
        SkeletonService.setCaseSkeletons(place.id!, amount, MEDIA_TYPES.PHOTO);
        break;
      case UPLOAD_PLACES.DIALOG:
        SkeletonService.setDialogSkeletons(place.id!, amount, MEDIA_TYPES.FILES);
        break;
      case UPLOAD_PLACES.DIALOG_PHOTO:
        SkeletonService.setDialogSkeletons(place.id!, amount, MEDIA_TYPES.PHOTO);
        break;
      case UPLOAD_PLACES.FOLDER:
        SkeletonService.setFolderSkeletons(place.id!, amount, MEDIA_TYPES.FILES);
        break;
      case UPLOAD_PLACES.FOLDER_PHOTO:
        SkeletonService.setFolderSkeletons(place.id!, amount, MEDIA_TYPES.PHOTO);
        break;
      case UPLOAD_PLACES.MIXED:
        SkeletonService.setMixedSkeletons(amount);
        break;
      case UPLOAD_PLACES.MIXED_PHOTO:
        SkeletonService.setMixedSkeletons(amount);
        SkeletonService.setPhotoSkeletons(amount);
        break;
      case UPLOAD_PLACES.PHOTO:
        SkeletonService.setPhotoSkeletons(amount);
        break;
      default:
        break;
    }
  }

  private static isUploadLimits(filesAmount: number, isAvatar: boolean): boolean {
    const mainStore = useMainStore();

    const isHardQuotaLimit = mainStore.subscriptionQuota.filesQuota.current + filesAmount >= mainStore.subscriptionQuota.filesQuota.maxHard;
    const isSoftQuotaLimit = mainStore.subscriptionQuota.filesQuota.current + filesAmount >= mainStore.subscriptionQuota.filesQuota.maxSoft;
    const isPremium = mainStore.user.accountStatuses.includes(ACCOUNT_STATUS.PREMIUM);
    if (!isPremium && !isAvatar) {
      if (isHardQuotaLimit) {
        mainStore.hardQuotaWarning = true;
        return true;
      } if (isSoftQuotaLimit) {
        mainStore.softQuotaWarning = true;
        return true;
      }
    }

    return false;
  }

  private static getSource(isCamera: boolean, isDesktop: boolean) {
    if (isDesktop) {
      return UPLOAD_SOURCE.DESKTOP;
    }
    return isCamera ? UPLOAD_SOURCE.PHONE_CAMERA : UPLOAD_SOURCE.PHONE_LIBRARY;
  }

  // use composable 'UseUpload' to get onUploadUpdate function
  public static async batchUploadTUS(
    fileUploads: IFileUpload[],
    onUploadUpdate: () => IOnUploadUpdate,
    place: {location: UPLOAD_PLACES, id?: string},
    folderId?: string,
    labels = [] as ISearchLabelInput[],
    isCamera = false,
    isAvatar = false,
  )
  : Promise<IMedia[]> {
    const mainStore = useMainStore();
    const mediaList: IMedia[] = [];

    if (this.isUploadLimits(mediaList.length, isAvatar) && !isAvatar) {
      return [];
    }

    onUploadUpdate().setStartInfo(fileUploads); // set start upload info
    this.setSkeletons(place, fileUploads.length); // set skeletons

    const uploadInfoId = uuidv4() as string;

    onUploadUpdate().getUploadInfo().id = uploadInfoId;

    mainStore.uploadInfos.push(onUploadUpdate().getUploadInfo());

    // declare main upload process function
    const processUpload = (uploadItem: IFileUpload, isSingle: boolean) => new Promise<void>((resolve, reject) => {
      // set interval to check if aborting
      const interval = this.listenUploadAborting(onUploadUpdate, reject);

      const source = this.getSource(isCamera, !mainStore.isNative);
      this.getItemLabels(uploadItem, labels)
        .then((labelsId) => this.tusFileUpload(uploadItem.file, onUploadUpdate, folderId, isSingle, source, isAvatar, UPLOAD_PLACES[place.location])
          .then((uuid) => { // TUS cannot give as any response data, so we have to get it manually
            this.getUploadedFile(uuid)
              .then((response) => {
                if (!response.success || !response.results.data) {
                  onUploadUpdate().addError();
                  this.removeSkeletons(place, fileUploads.length); // set skeletons
                  reject();
                  return;
                }
                onUploadUpdate().addSuccess();
                if (!isAvatar) {
                  mainStore.subscriptionQuota.filesQuota.current += 1;
                }
                mediaList.push(response.results.data);
                this.checkCrop(response.results.data.id, uploadItem.coodrinates).then(() => { // if crop coodrinates - crop
                  this.updateStorage(response.results.data, place); // manually update pinia storage to show uploaded files
                  this.removeSkeletons(place); // remove skeleton
                  this.checkLabels(response.results.data.id, labelsId); // if labels - add
                })
                  .finally(() => {
                    clearInterval(interval);
                    resolve();
                  });
              });
          })
          .catch((error) => {
            onUploadUpdate().addError();
            this.removeSkeletons(place, fileUploads.length); // set skeletons
          }));
    });

    // split array to two-dimensional array for paralel upload using Promise.all(), pack size can be adjusted changing const PARALEL_FILES_UPLOAD_SIZE
    const isSingle = fileUploads.length === 1;
    const paralels = this.toParalelUpload(fileUploads);
    return paralels
      .reduce( // we use reduce to sequential fetch №5 method https://habr.com/ru/post/490524/
        (accum, filesPack) => accum.then(() => Promise.all(filesPack.map((pack) => processUpload(pack, isSingle)))), // paralel upload pack of files
        Promise.all([].map(() => Promise.resolve())), // We need initialValue as "Promise.all( [].map(() => Promise.resolve()) )" to resolve typescript errors
      )
      .then(() => mediaList) // return mediaList as function response
      .finally(() => {
        this.isAbortUpload = false;
        onUploadUpdate().clear();
        mainStore.uploadInfos = mainStore.uploadInfos.filter((info) => info.id !== uploadInfoId);
        if (!mainStore.uploadInfos.length) {
          // NotificationService.notifySuccess();
        }
      });
  }

  private static updateStorage(file: IMedia, place: {location: UPLOAD_PLACES, id?: string}) {
    const driveStore = useDriveStore();
    const isFolder = place.location === UPLOAD_PLACES.FOLDER_PHOTO || place.location === UPLOAD_PLACES.FOLDER;
    const isProfilePhoto = place.location === UPLOAD_PLACES.PROFILE_PHOTO;

    // declare functions to easy read
    function createMixedWrapper(): IMixedData {
      return {
        id: file.id,
        type: MIXED_DATA_TYPE.FILE,
        file,
      };
    }

    function updateFolder() {
      const mixed = createMixedWrapper();

      if (file.type !== MEDIA_TYPES.FILES) {
        driveStore.photos.data = [file, ...driveStore.photos.data];
      }

      if (place.id && driveStore.currentFolder?.id === place.id) {
        if (driveStore.currentFolder.mixedData) {
          driveStore.currentFolder.mixedData = [mixed, ...driveStore.currentFolder.mixedData];
        } else {
          driveStore.currentFolder.mixedData = [mixed];
        }
      }

      const globalFolder = driveStore.mixedData.data.find((fold) => fold.id === place.id);
      if (globalFolder?.folder) {
        if (globalFolder.folder.mixedData) {
          globalFolder.folder.mixedData = [mixed, ...globalFolder.folder.mixedData];
        } else {
          globalFolder.folder.mixedData = [mixed];
        }
      }
    }

    function updateDrive() {
      const mixed = createMixedWrapper();
      const notFolderIdx = driveStore.mixedData.data.findIndex((item) => !item.folder);
      if (notFolderIdx !== -1) {
        driveStore.mixedData.data.splice(notFolderIdx, 0, mixed);
      } else {
        driveStore.mixedData.data = [...driveStore.mixedData.data, mixed];
      }

      if (file.type !== MEDIA_TYPES.FILES) {
        driveStore.photos.data = [file, ...driveStore.photos.data];
      } else {
        driveStore.files = [file, ...driveStore.files];
      }
    }

    // main logic
    if (place.id && isFolder) {
      updateFolder();
    } else if (!isProfilePhoto) {
      updateDrive();
    }
  }

  public static cropPhoto(id: string, photoCrop: ICropCoordinates): Promise<IResponse> {
    return ApiService.postAndReturnData('files/crop', { id, photoCrop });
  }

  public static getUploadedFile(fileId: string): Promise<IResponse<IData<IMedia>>> {
    return ApiService.getAndReturnData(`/files/check-uploaded/${fileId}`);
  }
}
